import { Box, CircularProgress, Typography } from '@mui/material';
import {
    Button,
    Dialog,
    MultiStepForm,
    SIMDocumentTypeAttachmentOption,
    isBuyerPortalFeatureEnabled,
    isUserLoggedIn,
    questionnaireHasFilledAnswers,
    useBuyerPortalSettings,
    useBuyerQuestionnaires,
    useCompany,
    useQuestionnaireSimSkeletons,
    useValidateAccount,
    useValidateTin,
} from 'common';
import { useSupplierPendingSimRequests } from './useSupplierPendingSimRequests';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { routes } from '../../../signup-portal.routes';
import { useCompanyPublicData } from '../../../services';
import {
    PostSignupFormData,
    buildPostSignupFormSchema,
    emptyCertificateOfInsuranceFormViewModel,
    emptyRemittanceFormViewModel,
    createEmptyTaxDetailsFormViewModel,
} from './steps';
import { useQueryClient } from '@tanstack/react-query';
import { buildPostSignupPipeline } from './postSignupPipeline';
import { clearPostSignupSession } from './clearPostSignupSession';
import { useSilentSignIn } from './useSilentSignIn';
import { PostSignupSessionTimer } from './PostSignupSessionTimer';
import { resolveQuestionnaireAttachmentOptions, resolveSimAttachmentOptions } from './simAttachmentOptions';
import { SimSubmissionContext } from './_shared/simDocumentVM';
import { useEnsureSimCustomers } from './useEnsureSimCustomers';
import { useSimSkeletons } from './useSimSkeletons';
import { useSimSubmissionProfiles } from './useSimSubmissionProfiles';
import {
    AgreementStepKey,
    PostSignupAgreement,
    isAgreementNeeded,
    usePostSignupAgreements,
} from './usePostSignupAgreements';
import { SimSubmitProgress, useSubmitSimDocument } from './submitSimDocument';
import { extractPaymentFieldConfiguration } from './steps/Remittance/Remittance.utils';
import {
    ReviewRowLabel,
    SubmissionDoc,
    SubmissionDocKey,
    SubmissionState,
    buildDocSubmissionPlan,
} from './steps/ReviewAndSubmit';
import { useVatArchiveService } from '../SupplierSignUp/useVatArchiveService';
import { SubmissionGuideLocationState } from '../SubmissionGuide';

type LocationState = {
    companyId: number;
    companyName: string;
    vendorId: number | null;
    buyerCompanyId: number;
    submissionMethodKey: string;
} & (
    | { alreadySignedIn?: false; loginEmail: string; password: string }
    // The landing page's finish-documents dialog signs in before navigating here.
    | { alreadySignedIn: true }
);

const createDefaultFormValues = (companyName: string): PostSignupFormData => ({
    taxDetails: createEmptyTaxDetailsFormViewModel(companyName),
    remittance: {
        ...emptyRemittanceFormViewModel,
        BusinessName: companyName,
    },
    certificateOfInsurance: emptyCertificateOfInsuranceFormViewModel,
    questionnaires: [],
});

const SECTION_LABELS: Record<string, string> = {
    taxDetails: 'Tax Details',
    remittance: 'Remittance',
    certificateOfInsurance: 'Insurance',
    questionnaires: 'Questionnaires',
};

type SimSubmissionBaseContext = Omit<SimSubmissionContext, 'customerId' | 'customerNumber'>;

/** `sent` = every document delivered; `skipped` = delivered, but the user skipped at least one. */
type CompletionState = 'sent' | 'skipped' | null;

function toNames(files: File[] | undefined): string[] {
    return (files ?? []).map((file) => file.name);
}

export function PostSignupOrchestrator() {
    const { companyToken = '' } = useParams<{ companyToken?: string }>();
    const location = useLocation<LocationState | undefined>();
    const history = useHistory();
    const queryClient = useQueryClient();
    const { signIn, isLoading: isSigningIn, isSuccess: isSilentSignInSuccess, isError } = useSilentSignIn();
    const hasStartedSignIn = useRef(false);
    // Checked once so an expiring token doesn't unmount the form; SessionTimer handles expiry.
    const [hasExistingSession] = useState(() => location.state?.alreadySignedIn === true && isUserLoggedIn());
    const isSignedIn = hasExistingSession || isSilentSignInSuccess;

    const [stepError, setStepError] = useState<string | null>(null);
    const [submissionStates, setSubmissionStates] = useState<Record<SubmissionDocKey, SubmissionState>>({});
    const [attachments, setAttachments] = useState<Record<SubmissionDocKey, File[]>>({});
    /** Doc keys whose Required dropzone was empty when the user tried to advance. */
    const [attachmentErrors, setAttachmentErrors] = useState<Record<SubmissionDocKey, boolean>>({});
    const [isSaving, setIsSaving] = useState(false);
    /** Non-null once every document is sent or skipped; the dialog holds the user before the guide. */
    const [completion, setCompletion] = useState<CompletionState>(null);
    /** Per-doc draft id + whether its attachments landed, so retry reuses the draft without re-uploading or skipping. */
    const submitProgressRef = useRef<Partial<Record<SubmissionDocKey, SimSubmitProgress>>>({});
    /** Shares a successful TIN result across Tax/Remittance retries for the unchanged value pair. */
    const tinValidationCacheRef = useRef(new Map<string, Promise<string | null>>());
    /** Steps whose provider agreement the supplier ticked; all are signed when Submit starts, before any document. */
    const [acceptedAgreements, setAcceptedAgreements] = useState<Partial<Record<AgreementStepKey, boolean>>>({});

    const locationState = location.state;

    // Primed upstream by landing/signup pages with Infinity staleTime, so this is typically a cache hit.
    const { data: buyerCompany } = useCompanyPublicData({ search: companyToken });
    const { data: buyerPortalSettings } = useBuyerPortalSettings({
        companyId: locationState?.buyerCompanyId ?? null,
    });

    const {
        flags: pendingFlags,
        activeDocumentClasses,
        schedules: pendingSchedules,
        simRequests,
        isLoading: isCheckingPendingRequests,
        isError: isPendingRequestsError,
    } = useSupplierPendingSimRequests({
        supplierCompanyId: locationState?.companyId ?? null,
        buyerCompanyId: locationState?.buyerCompanyId ?? null,
        enabled: isSignedIn,
    });

    const {
        data: allBuyerQuestionnaires = [],
        isLoading: isLoadingBuyerQuestionnaires,
        isError: isBuyerQuestionnairesError,
    } = useBuyerQuestionnaires({
        buyerCompanyId: locationState?.buyerCompanyId,
        enabled: isSignedIn,
    });

    /**
     * Restrict to questionnaires the buyer has actually scheduled for this
     * supplier. Mirrors the W-9 / COI / Remittance flag logic: a
     * questionnaire is "pending" only when its `BusinessDocSubTypeId`
     * appears as a `DocumentClass` on a schedule with an open reminder.
     */
    const pendingQuestionnaires = useMemo(
        () =>
            allBuyerQuestionnaires.filter((questionnaire) => {
                const subTypeId = questionnaire.businessDocTypeViewModel.BusinessDocSubTypeId;
                return subTypeId != null && activeDocumentClasses.has(subTypeId);
            }),
        [allBuyerQuestionnaires, activeDocumentClasses]
    );

    const questionnairesBySubTypeId = useMemo(
        () =>
            new Map(
                pendingQuestionnaires.map((questionnaire) => [
                    questionnaire.businessDocTypeViewModel.BusinessDocSubTypeId!,
                    questionnaire,
                ])
            ),
        [pendingQuestionnaires]
    );

    const questionnaireClassIds = useMemo(
        () => Array.from(questionnairesBySubTypeId.keys()),
        [questionnairesBySubTypeId]
    );

    const allowTaxAndRemittanceCreationOnBackgroundCheckFailure = isBuyerPortalFeatureEnabled(
        buyerPortalSettings,
        'AllowTaxAndRemittanceCreationOnBackgroundCheckFailure'
    );

    const clientSideCustomerCreationEnabled = isBuyerPortalFeatureEnabled(
        buyerPortalSettings,
        'EnableClientSideCustomerCreation'
    );

    // TEMPORARY: drop once backend resolves SIM CustomerId server-side.
    const {
        customerIds: simCustomerIds,
        customerNumbers: simCustomerNumbers,
        questionnaireCustomerIds,
        questionnaireCustomerNumbers,
        isLoading: isLoadingSimCustomers,
        isError: isSimCustomersError,
    } = useEnsureSimCustomers({
        supplierCompanyId: locationState?.companyId ?? null,
        buyerCompanyId: locationState?.buyerCompanyId ?? null,
        flags: pendingFlags,
        schedules: pendingSchedules,
        questionnaireClassIds,
        clientSideCustomerCreationEnabled,
        enabled: isSignedIn && !isCheckingPendingRequests && !isLoadingBuyerQuestionnaires,
    });

    const { data: fullBuyerCompanyData } = useCompany({
        companyId: locationState?.buyerCompanyId ?? null,
        enabled: isSignedIn,
    });

    const paymentFieldConfiguration = useMemo(
        () => extractPaymentFieldConfiguration(fullBuyerCompanyData?.VendorCustomAttributes ?? null),
        [fullBuyerCompanyData]
    );

    const {
        senderProfileId,
        receiverProfileId,
        isLoading: isLoadingProfiles,
        isError: isProfileError,
    } = useSimSubmissionProfiles({
        supplierCompanyId: locationState?.companyId ?? null,
        buyerCompanyId: locationState?.buyerCompanyId ?? null,
        enabled: isSignedIn,
    });

    const {
        getStepAgreement,
        recordSignature,
        isLoading: isLoadingAgreements,
        isError: isAgreementsError,
    } = usePostSignupAgreements({
        supplierCompanyId: locationState?.companyId ?? null,
        enabled: isSignedIn,
    });

    const handleAgreementAcceptedChange = useCallback((stepKey: AgreementStepKey, accepted: boolean) => {
        setAcceptedAgreements((prev) => ({ ...prev, [stepKey]: accepted }));
    }, []);

    // Customer-creation hook is gated by SIM flags; questionnaire skeletons
    // don't need customer ids, so default to empties so they aren't blocked.
    const skeletonContext =
        locationState && isSignedIn
            ? {
                  buyerCompanyId: locationState.buyerCompanyId,
                  supplierCompanyId: locationState.companyId,
                  customerIds: simCustomerIds ?? {
                      taxDetails: null,
                      remittance: null,
                      certificateOfInsurance: null,
                  },
                  clientSideCustomerCreationEnabled,
              }
            : null;

    const skeletons = useSimSkeletons(pendingFlags, skeletonContext);

    const questionnaireSkeletons = useQuestionnaireSimSkeletons(pendingQuestionnaires, skeletonContext);

    const { submit: submitSimDocument } = useSubmitSimDocument();

    const { mutateAsync: validateAccount } = useValidateAccount({
        SupplierCompanyId: locationState?.companyId ?? null,
        VendorId: locationState?.vendorId ?? null,
        BuyerCompanyId: locationState?.buyerCompanyId ?? null,
    });

    const { mutateAsync: validateTin } = useValidateTin({
        SupplierCompanyId: locationState?.companyId ?? null,
        VendorId: locationState?.vendorId ?? null,
        BuyerCompanyId: locationState?.buyerCompanyId ?? null,
    });

    const submissionBaseContext: SimSubmissionBaseContext | null = useMemo(() => {
        if (!locationState || senderProfileId == null || receiverProfileId == null) {
            return null;
        }
        return {
            buyerCompanyId: locationState.buyerCompanyId,
            supplierCompanyId: locationState.companyId,
            supplierCompanyName: locationState.companyName,
            buyerCompanyName: buyerCompany?.Name ?? null,
            senderProfileId,
            receiverProfileId,
        };
    }, [locationState, buyerCompany?.Name, senderProfileId, receiverProfileId]);

    const reviewRows: ReviewRowLabel[] = useMemo(() => {
        const rows: ReviewRowLabel[] = [];
        if (pendingFlags.taxDetails) {
            rows.push({ key: 'taxDetails', label: 'Tax Details', attachmentNames: toNames(attachments.taxDetails) });
        }
        if (pendingFlags.remittance) {
            rows.push({ key: 'remittance', label: 'Remittance', attachmentNames: toNames(attachments.remittance) });
        }
        if (pendingFlags.certificateOfInsurance) {
            rows.push({
                key: 'certificateOfInsurance',
                label: 'Certificate of Insurance',
                attachmentNames: toNames(attachments.certificateOfInsurance),
            });
        }
        for (const questionnaire of pendingQuestionnaires) {
            const subTypeId = questionnaire.businessDocTypeViewModel.BusinessDocSubTypeId;
            if (subTypeId == null) {
                continue;
            }
            rows.push({
                key: `questionnaire:${subTypeId}`,
                label: questionnaire.name,
                attachmentNames: toNames(attachments[`questionnaire:${subTypeId}`]),
            });
        }
        return rows;
    }, [attachments, pendingFlags, pendingQuestionnaires]);

    const { data: vatArchiveServiceData = [] } = useVatArchiveService({
        companyId: locationState?.companyId,
        $filter: 'InvoiceProfileId eq null',
    });

    const tinValidityEnabled = isBuyerPortalFeatureEnabled(buyerPortalSettings, 'EnablePostSignupTinValidation');
    const tinFraudEnabled = isBuyerPortalFeatureEnabled(buyerPortalSettings, 'EnablePostSignupTinFraudCheck');
    const achValidationEnabled = isBuyerPortalFeatureEnabled(buyerPortalSettings, 'EnablePostSignupAchValidation');

    // An agreement only applies when the buyer runs the check it unlocks.
    const stepAgreements = useMemo<Partial<Record<AgreementStepKey, PostSignupAgreement>>>(
        () => ({
            taxDetails: tinValidityEnabled || tinFraudEnabled ? getStepAgreement('taxDetails') : undefined,
            remittance: achValidationEnabled ? getStepAgreement('remittance') : undefined,
        }),
        [achValidationEnabled, getStepAgreement, tinFraudEnabled, tinValidityEnabled]
    );

    const buildPlanFor = useCallback(
        (formData: PostSignupFormData): SubmissionDoc[] => {
            if (!submissionBaseContext) {
                return [];
            }
            return buildDocSubmissionPlan({
                formData,
                flags: pendingFlags,
                skeletons,
                questionnaireSkeletons: questionnaireSkeletons.skeletonsBySubTypeId,
                questionnairesBySubTypeId,
                simRequests,
                baseContext: submissionBaseContext,
                customerIds: simCustomerIds,
                customerNumbers: simCustomerNumbers,
                questionnaireCustomerIds,
                questionnaireCustomerNumbers,
                attachmentsByDocKey: attachments,
                schedules: pendingSchedules,
                allowTaxAndRemittanceCreationOnBackgroundCheckFailure,
                // Buyer opt-in via FeaturesEnabled.
                validateAccount: achValidationEnabled ? validateAccount : undefined,
                validateTin: tinValidityEnabled || tinFraudEnabled ? validateTin : undefined,
                tinChecks: { name: tinValidityEnabled, watchlist: tinFraudEnabled },
                tinValidationCache: tinValidationCacheRef.current,
                clientSideCustomerCreationEnabled,
                vatArchiveServiceData,
                supplierCompanyId: locationState?.companyId,
            });
        },
        [
            achValidationEnabled,
            attachments,
            clientSideCustomerCreationEnabled,
            pendingFlags,
            pendingSchedules,
            questionnaireCustomerIds,
            questionnaireCustomerNumbers,
            questionnaireSkeletons.skeletonsBySubTypeId,
            questionnairesBySubTypeId,
            simRequests,
            simCustomerIds,
            simCustomerNumbers,
            skeletons,
            submissionBaseContext,
            tinFraudEnabled,
            tinValidityEnabled,
            allowTaxAndRemittanceCreationOnBackgroundCheckFailure,
            validateAccount,
            validateTin,
            vatArchiveServiceData,
            locationState?.companyId,
        ]
    );

    const submitDoc = useCallback(
        async (doc: SubmissionDoc): Promise<SubmissionState> => {
            const body = doc.build();
            if (!body) {
                return { status: 'error', errorMessage: `"${doc.label}" could not be built.` };
            }
            try {
                if (doc.validate) {
                    const validationError = await doc.validate();
                    if (validationError) {
                        return { status: 'error', errorMessage: validationError };
                    }
                }
                const result = await submitSimDocument(body, submitProgressRef.current[doc.key], doc.files);
                submitProgressRef.current[doc.key] = {
                    draftId: result.draftId,
                    attachmentsUploaded: result.attachmentsUploaded,
                };
                if (result.sent) {
                    if (doc.onSuccessfulSubmission) {
                        doc.onSuccessfulSubmission().catch((err) => {
                            console.error(`[PostSignup] "${doc.label}" post submission callback failed`, err);
                        });
                    }
                    return { status: 'success' };
                }
                if (result.failedStage === 'attachments') {
                    return {
                        status: 'draft',
                        errorMessage:
                            `"${doc.label}" was saved as a draft, but its supporting documents could not be ` +
                            'uploaded, so it was not submitted. Use Retry below.',
                    };
                }
                return {
                    status: 'draft',
                    errorMessage:
                        `"${doc.label}" was saved as a draft but could not be submitted. ` +
                        'Use Retry below or finish it from the Supplier Portal.',
                };
            } catch (err) {
                // eslint-disable-next-line no-console
                console.error(`[PostSignup] "${doc.label}" submission failed`, err);
                const message = err instanceof Error ? err.message : 'Submission failed.';
                return { status: 'error', errorMessage: message };
            }
        },
        [submitSimDocument]
    );

    const isResolvingQuestionnaireStep =
        isSignedIn && locationState?.buyerCompanyId != null && isLoadingBuyerQuestionnaires;

    const isLoading =
        isSigningIn ||
        (isSignedIn && isCheckingPendingRequests) ||
        (isSignedIn && isResolvingQuestionnaireStep) ||
        (isSignedIn && isLoadingSimCustomers) ||
        (isSignedIn && isLoadingAgreements) ||
        (isSignedIn && skeletons.isLoading) ||
        (isSignedIn && questionnaireSkeletons.isLoading) ||
        (isSignedIn && isLoadingProfiles);

    const landingPagePath = routes.supplierLandingPage.path.replace(':companyToken', companyToken);

    const returnToLandingPage = useCallback(() => {
        clearPostSignupSession(queryClient);
        history.replace(landingPagePath);
    }, [queryClient, history, landingPagePath]);

    useEffect(() => {
        if (locationState == null) {
            history.replace(landingPagePath);
        } else if (locationState.alreadySignedIn && !hasExistingSession) {
            returnToLandingPage();
        }
    }, [locationState, history, landingPagePath, hasExistingSession, returnToLandingPage]);

    useEffect(() => {
        if (locationState == null || locationState.alreadySignedIn || hasStartedSignIn.current) {
            return;
        }
        hasStartedSignIn.current = true;
        signIn(locationState.loginEmail, locationState.password);
    }, [locationState, signIn]);

    const guidePath = locationState?.submissionMethodKey
        ? routes.supplierInvoiceSubmissionGuide.path
              .replaceAll(':companyToken', companyToken)
              .replaceAll(':submissionMethod', locationState.submissionMethodKey)
        : landingPagePath;

    const navigateToGuide = useCallback(() => {
        clearPostSignupSession(queryClient);
        history.push(guidePath);
    }, [queryClient, history, guidePath]);

    /**
     * Any setup-time failure (sign-in, schedules, profiles, skeletons)
     * drops the supplier into the submission guide. The account
     * is already created at this point, so the guide is the safest landing.
     */
    const hasSetupError =
        isError ||
        isPendingRequestsError ||
        isBuyerQuestionnairesError ||
        isProfileError ||
        isSimCustomersError ||
        isAgreementsError ||
        skeletons.isError ||
        questionnaireSkeletons.isError;
    useEffect(() => {
        if (hasSetupError) {
            clearPostSignupSession(queryClient);
            const state: SubmissionGuideLocationState = { setupFailed: true };
            history.push(guidePath, state);
        }
    }, [hasSetupError, queryClient, history, guidePath]);

    const attachmentOptions = useMemo(() => resolveSimAttachmentOptions(pendingSchedules), [pendingSchedules]);

    const buyerName = buyerCompany?.Name ?? 'your customer';

    const completionMessage =
        completion === 'skipped'
            ? // TODO: confirm verbiage for the skipped case.
              `Your documents were sent to ${buyerName} — you skipped one or more items, which you can finish any time from the Supplier Portal.`
            : `Your documents were sent to ${buyerName}.`;

    const questionnaireAttachmentOptions = useMemo(
        () => resolveQuestionnaireAttachmentOptions(pendingSchedules, questionnaireClassIds),
        [pendingSchedules, questionnaireClassIds]
    );

    const handleAttachmentsChange = useCallback((key: SubmissionDocKey, files: File[]) => {
        setAttachments((prev) => ({ ...prev, [key]: files }));
        if (files.length > 0) {
            setAttachmentErrors((prev) => ({ ...prev, [key]: false }));
        }
    }, []);

    const handleSkip = useCallback((key: SubmissionDocKey) => {
        setSubmissionStates((prev) => ({ ...prev, [key]: { status: 'skipped' } }));
    }, []);

    // Step-scoped errors (failed submit, missing attachments) shouldn't
    // follow the user when they navigate to another step.
    const handleStepChange = useCallback(() => {
        setStepError(null);
    }, []);

    const handleRetry = useCallback(
        async (key: SubmissionDocKey, formData: PostSignupFormData) => {
            const doc = buildPlanFor(formData).find((d) => d.key === key);
            if (!doc) {
                return;
            }
            setSubmissionStates((prev) => ({ ...prev, [key]: { status: 'submitting' } }));
            const next = await submitDoc(doc);
            setSubmissionStates((prev) => ({ ...prev, [key]: next }));
        },
        [buildPlanFor, submitDoc]
    );

    const pipeline = useMemo(
        () =>
            buildPostSignupPipeline(pendingFlags, {
                buyerQuestionnaires: pendingQuestionnaires,
                isLoadingQuestionnaires: isLoadingBuyerQuestionnaires,
                attachmentOptions,
                questionnaireAttachmentOptions,
                attachments,
                attachmentErrors,
                onAttachmentsChange: handleAttachmentsChange,
                reviewRows,
                submissionStates,
                onRetry: handleRetry,
                onSkip: handleSkip,
                paymentFieldConfiguration,
                isSaving,
                stepAgreements,
                acceptedAgreements,
                onAgreementAcceptedChange: handleAgreementAcceptedChange,
            }),
        [
            acceptedAgreements,
            attachmentOptions,
            questionnaireAttachmentOptions,
            attachments,
            attachmentErrors,
            handleAgreementAcceptedChange,
            handleAttachmentsChange,
            handleRetry,
            handleSkip,
            isLoadingBuyerQuestionnaires,
            isSaving,
            pendingFlags,
            pendingQuestionnaires,
            reviewRows,
            stepAgreements,
            submissionStates,
            paymentFieldConfiguration,
        ]
    );

    /**
     * Steps don't make their own API calls (Review & Submit handles
     * submission), but a step whose schedule requires attachments blocks
     * Next until at least one file is attached.
     */
    const handleBeforeNext = useCallback(
        async (stepIndex: number, formData: PostSignupFormData) => {
            const stepDocKey = pipeline[stepIndex]?.fields[0];

            // The questionnaires step holds one document per questionnaire, each with its own schedule.
            // Skip-empty questionnaires are not submitted, so Required attachments only apply when
            // the supplier actually filled answers (or we would block skipping a blank form).
            if (stepDocKey === 'questionnaires') {
                const missing = pendingQuestionnaires.flatMap((questionnaire) => {
                    const subTypeId = questionnaire.businessDocTypeViewModel.BusinessDocSubTypeId;
                    if (
                        subTypeId == null ||
                        questionnaireAttachmentOptions[subTypeId] !== SIMDocumentTypeAttachmentOption.Required
                    ) {
                        return [];
                    }
                    const entry = formData.questionnaires.find((item) => item.businessDocSubTypeId === subTypeId);
                    if (!questionnaireHasFilledAnswers(questionnaire.items, entry?.answers)) {
                        return [];
                    }
                    if ((attachments[`questionnaire:${subTypeId}`] ?? []).length > 0) {
                        return [];
                    }
                    return [subTypeId];
                });
                if (missing.length > 0) {
                    setAttachmentErrors((prev) => ({
                        ...prev,
                        ...Object.fromEntries(missing.map((subTypeId) => [`questionnaire:${subTypeId}`, true])),
                    }));
                    setStepError('Please attach the required supporting documents before continuing.');
                    return false;
                }
                setStepError(null);
                return true;
            }

            if (stepDocKey === 'taxDetails' || stepDocKey === 'remittance') {
                const agreement = stepAgreements[stepDocKey];
                if (
                    agreement != null &&
                    !agreement.IsSigned &&
                    !acceptedAgreements[stepDocKey] &&
                    isAgreementNeeded(stepDocKey, formData)
                ) {
                    setStepError(`Please accept the ${agreement.serviceAgreement?.Name} to continue.`);
                    return false;
                }
            }

            if (stepDocKey !== 'taxDetails' && stepDocKey !== 'remittance' && stepDocKey !== 'certificateOfInsurance') {
                return true;
            }
            const isRequired = attachmentOptions[stepDocKey] === SIMDocumentTypeAttachmentOption.Required;
            if (isRequired && (attachments[stepDocKey] ?? []).length === 0) {
                setAttachmentErrors((prev) => ({ ...prev, [stepDocKey]: true }));
                setStepError('Please attach at least one supporting document before continuing.');
                return false;
            }
            setStepError(null);
            return true;
        },
        [
            acceptedAgreements,
            attachmentOptions,
            attachments,
            pendingQuestionnaires,
            pipeline,
            questionnaireAttachmentOptions,
            stepAgreements,
        ]
    );

    /**
     * Runtime form schema with per-questionnaire required-field rules.
     * Stable for the lifetime of the form: questionnaires are fetched
     * once before <MultiStepForm/> mounts, then cached infinitely.
     */
    const postSignupFormSchema = useMemo(
        () => buildPostSignupFormSchema(pendingQuestionnaires, pendingFlags),
        [pendingQuestionnaires, pendingFlags]
    );

    useEffect(() => {
        if (!isSignedIn || isCheckingPendingRequests || isResolvingQuestionnaireStep || hasSetupError) {
            return;
        }
        if (pipeline.length === 0) {
            navigateToGuide();
        }
    }, [isSignedIn, isCheckingPendingRequests, isResolvingQuestionnaireStep, hasSetupError, pipeline, navigateToGuide]);

    const isPlanFullySubmitted = useCallback(
        (plan: SubmissionDoc[], states: Record<SubmissionDocKey, SubmissionState>) =>
            plan.every((doc) => {
                const status = states[doc.key]?.status;
                return status === 'success' || status === 'skipped';
            }),
        []
    );

    const resolveCompletion = useCallback(
        (plan: SubmissionDoc[], states: Record<SubmissionDocKey, SubmissionState>): CompletionState =>
            plan.some((doc) => states[doc.key]?.status === 'skipped') ? 'skipped' : 'sent',
        []
    );

    const handleSubmit = useCallback(
        async (formData: PostSignupFormData) => {
            if (!submissionBaseContext) {
                setStepError('Missing signup context; please restart the signup flow.');
                return;
            }
            const plan = buildPlanFor(formData);
            const targets = plan.filter((doc) => {
                const status = submissionStates[doc.key]?.status;
                return status !== 'success' && status !== 'skipped';
            });
            if (targets.length === 0) {
                if (isPlanFullySubmitted(plan, submissionStates)) {
                    setCompletion(resolveCompletion(plan, submissionStates));
                }
                return;
            }

            setStepError(null);
            setIsSaving(true);

            const agreementsToSign = (Object.keys(stepAgreements) as AgreementStepKey[])
                .filter((stepKey) => acceptedAgreements[stepKey] && isAgreementNeeded(stepKey, formData))
                .map((stepKey) => stepAgreements[stepKey])
                .filter((agreement): agreement is PostSignupAgreement => agreement != null && !agreement.IsSigned);
            try {
                await Promise.all(agreementsToSign.map(recordSignature));
            } catch {
                setStepError("We couldn't record your agreement. Please try Submit again.");
                setIsSaving(false);
                return;
            }

            setSubmissionStates((prev) => {
                const next = { ...prev };
                for (const doc of targets) {
                    next[doc.key] = { status: 'submitting' };
                }
                return next;
            });

            const results = await Promise.all(
                targets.map(async (doc) => {
                    const state = await submitDoc(doc);
                    // Each row flips as its own submission settles.
                    setSubmissionStates((prev) => ({ ...prev, [doc.key]: state }));
                    return [doc, state] as const;
                })
            );

            const nextStates: Record<SubmissionDocKey, SubmissionState> = { ...submissionStates };
            for (const [doc, state] of results) {
                nextStates[doc.key] = state;
            }

            setIsSaving(false);

            if (isPlanFullySubmitted(plan, nextStates)) {
                setCompletion(resolveCompletion(plan, nextStates));
            } else {
                setStepError(
                    'Some documents could not be sent. Retry or Skip the failed items, then click Submit again to continue to the next page.'
                );
            }
        },
        [
            acceptedAgreements,
            buildPlanFor,
            isPlanFullySubmitted,
            recordSignature,
            resolveCompletion,
            stepAgreements,
            submissionBaseContext,
            submissionStates,
            submitDoc,
        ]
    );

    /**
     * Intermediate steps only gate on their own fields, so the form can
     * reach Review & Submit while another step is still invalid. Surface
     * that here instead of letting the Submit click silently no-op.
     */
    const handleInvalidSubmit = useCallback((errors: Record<string, unknown>) => {
        // eslint-disable-next-line no-console
        console.error('[PostSignup] Submission blocked by validation errors', errors);
        const sections = Object.keys(errors)
            .map((key) => SECTION_LABELS[key] ?? key)
            .join(', ');
        setStepError(
            `Some required information is missing or invalid in: ${sections}. ` +
                'Use Previous to go back and complete those steps.'
        );
    }, []);

    const defaultFormValues = useMemo(
        () => createDefaultFormValues(locationState?.companyName ?? ''),
        [locationState?.companyName]
    );

    if (locationState == null) {
        return null;
    }

    return (
        <Box
            sx={{
                height: '100%',
                minHeight: 0,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
            }}
        >
            {isSignedIn && <PostSignupSessionTimer onLogout={returnToLandingPage} />}
            {isLoading && (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 3,
                        flex: 1,
                    }}
                >
                    <CircularProgress />
                    <Typography variant="h6">Setting up your account...</Typography>
                </Box>
            )}

            {isSignedIn &&
                !hasSetupError &&
                pipeline.length > 0 &&
                !skeletons.isLoading &&
                !questionnaireSkeletons.isLoading &&
                !isLoadingProfiles &&
                !isResolvingQuestionnaireStep &&
                !isLoadingSimCustomers &&
                !isLoadingAgreements &&
                submissionBaseContext != null && (
                    <MultiStepForm
                        containerSx={{
                            p: {
                                xs: 0,
                                sm: 4,
                            },
                            minHeight: 0,
                            width: '100%',
                        }}
                        schema={postSignupFormSchema}
                        steps={pipeline}
                        defaultValues={defaultFormValues}
                        submitHandler={handleSubmit}
                        onInvalidSubmit={handleInvalidSubmit}
                        onBeforeNext={handleBeforeNext}
                        onStepChange={handleStepChange}
                        onCancel={() => navigateToGuide()}
                        shouldDisableNext={() => isSaving}
                        shouldDisableBack={() => isSaving}
                        shouldDisableCancel={() => isSaving}
                        formAlertProps={{
                            errorMessage: stepError,
                            warningMessage: null,
                            savingMessage: isSaving ? 'Submitting...' : null,
                            loadingMessage: null,
                            successMessage: null,
                        }}
                    />
                )}
            <Dialog
                open={completion != null}
                title={completion === 'skipped' ? 'Submission complete' : 'Documents submitted'}
                content={<Typography>{completionMessage}</Typography>}
                actions={<Button onClick={navigateToGuide}>Continue</Button>}
                handleOnClose={navigateToGuide}
                testId="post-signup-complete"
            />
        </Box>
    );
}
